import { isLocale, type Locale } from "../content/translations";
import { newsCategories, type NewsArticle, type NewsBlock, type NewsImage, type NewsTranslation } from "../content/news/types";

const record = (value: unknown): Record<string, unknown> => value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
const text = (value: unknown, max = 400): string => typeof value === "string" ? value.trim().slice(0, max) : "";
const list = (value: unknown): unknown[] => Array.isArray(value) ? value : [];

export function safeNewsLink(value: unknown): string | undefined {
  const href = text(value, 2048);
  try {
    const url = new URL(href);
    return ["https:", "http:", "mailto:", "tel:"].includes(url.protocol) && !url.username && !url.password ? url.href : undefined;
  } catch { return undefined; }
}

function localizedText(value: unknown): Partial<Record<Locale, string>> {
  const result: Partial<Record<Locale, string>> = {};
  for (const [language, content] of Object.entries(record(value))) {
    if (isLocale(language) && text(content)) result[language] = text(content);
  }
  return result;
}

function image(value: unknown, projectId: string, dataset: string): NewsImage {
  const entry = record(value), asset = record(entry.asset), dimensions = record(record(asset.metadata).dimensions);
  const url = new URL(text(asset.url, 2048));
  if (url.protocol !== "https:" || url.hostname !== "cdn.sanity.io" || url.username || url.password || !url.pathname.startsWith(`/images/${projectId}/${dataset}/`) || !/\.(jpg|jpeg|png|webp|avif|tif|tiff)$/i.test(url.pathname)) {
    throw new Error("A news image must be a raster asset from the configured Sanity project.");
  }
  const width = Number(dimensions.width), height = Number(dimensions.height);
  if (!Number.isInteger(width) || !Number.isInteger(height) || width < 1 || height < 1 || width * height > 256_000_000) throw new Error("A news image needs valid original dimensions.");
  url.search = ""; url.hash = "";
  return { url: url.href, width, height, alt: localizedText(entry.alt), caption: localizedText(entry.caption) };
}

function blocks(value: unknown): NewsBlock[] {
  if (list(value).length > 300) throw new Error("A news translation exceeds 300 text blocks. Split the content instead of truncating it.");
  return list(value).flatMap((raw, index) => {
    const block = record(raw);
    if (block._type !== "block") return [];
    const links: Record<string, string> = Object.create(null);
    for (const rawLink of list(block.markDefs)) {
      const link = record(rawLink), href = safeNewsLink(link.href), key = text(link._key, 80);
      if (link._type === "link" && href && /^[a-zA-Z0-9_-]+$/.test(key)) links[key] = href;
    }
    if (list(block.children).length > 1000) throw new Error("A news text block exceeds the span limit.");
    const children = list(block.children).flatMap(rawSpan => {
      const span = record(rawSpan);
      if (span._type !== "span" || typeof span.text !== "string") return [];
      if (span.text.length > 20000) throw new Error("A news text span exceeds 20,000 characters.");
      return [{ text: span.text, marks: list(span.marks).filter((mark): mark is string => typeof mark === "string" && (["strong", "em"].includes(mark) || Object.hasOwn(links,mark))) }];
    });
    if (!children.some(child => child.text.trim())) return [];
    const style = ["h2", "h3", "blockquote"].includes(String(block.style)) ? block.style as "h2" | "h3" | "blockquote" : "normal";
    const listStyle = block.listItem === "bullet" || block.listItem === "number" ? block.listItem : null;
    return [{key:text(block._key,80) || `block-${index}`,style,list:listStyle,children,links}];
  });
}

export function normalizeNewsDocuments(value: unknown, projectId: string, dataset: string, now = new Date()): NewsArticle[] {
  if (!Array.isArray(value)) throw new Error("Sanity did not return a news document array.");
  if (value.length > 10000) throw new Error("News documents exceed the configured free-plan safety limit.");
  const slugs = new Set<string>();
  const articles = value.flatMap(raw => {
    const doc = record(raw), id = text(doc._id, 160);
    if (!id || id.startsWith("drafts.") || id.startsWith("versions.") || doc._type !== "newsArticle") return [];
    const slug = text(record(doc.slug).current, 96);
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) throw new Error(`News document ${id} has an invalid URL slug.`);
    if (slugs.has(slug)) throw new Error(`Duplicate news URL: ${slug}`);
    const publishedAt = new Date(text(doc.publishedAt)).toISOString();
    if (new Date(publishedAt) > now) return [];
    const updatedAt = new Date(text(doc._updatedAt) || publishedAt).toISOString();
    const category = doc.category;
    if (!newsCategories.includes(category as typeof newsCategories[number])) throw new Error(`News document ${id} has an invalid category.`);
    const languages = new Set<string>();
    const translations: NewsTranslation[] = list(doc.translations).map(rawTranslation => {
      const entry = record(rawTranslation), language = entry.language;
      if (!isLocale(String(language))) throw new Error(`News document ${id} has an unsupported language.`);
      if (languages.has(String(language))) throw new Error(`News document ${id} has a duplicate language.`);
      languages.add(String(language));
      const title = text(entry.title,160), excerpt = text(entry.excerpt,400), body = blocks(entry.body);
      if (!title || !excerpt || !body.length) throw new Error(`News document ${id} needs a title, summary and body in ${language}.`);
      return {language:language as Locale,title,excerpt,body,seoTitle:text(entry.seoTitle,160) || undefined,seoDescription:text(entry.seoDescription,300) || undefined};
    });
    if (!translations.length) throw new Error(`News document ${id} needs at least one translation.`);
    slugs.add(slug);
    if (list(doc.gallery).length > 60) throw new Error(`News document ${id} exceeds the 60-photo gallery limit.`);
    return [{id,slug,category:category as typeof newsCategories[number],publishedAt,updatedAt,featured:doc.featured === true,cover:image(doc.coverImage,projectId,dataset),gallery:list(doc.gallery).map(rawImage => image(rawImage,projectId,dataset)),translations}];
  });
  return articles.sort((a,b) => b.publishedAt.localeCompare(a.publishedAt) || a.slug.localeCompare(b.slug));
}

export function newsImageUrl(image: NewsImage, width: number): string {
  const url = new URL(image.url);
  if (url.protocol !== "https:" || url.hostname !== "cdn.sanity.io" || !url.pathname.startsWith("/images/")) throw new Error("Untrusted news image URL.");
  url.searchParams.set("w", String(Math.min(image.width, Math.max(1, Math.round(width)))));
  url.searchParams.set("auto", "format"); url.searchParams.set("fit", "max"); url.searchParams.set("q", "85");
  return url.href;
}
